import React, { useRef } from 'react'
import Downshift from 'downshift'
import debounce from 'lodash/debounce'
import { RxCross2 } from 'react-icons/rx'
import { motion, AnimatePresence } from 'framer-motion'
import Link from 'next/link'

const SearchAutoComplete = ({
  items = [],
  placeholder = 'Search...',
  onSearch = () => {},
  onSelect = () => {},
  onEditIconClick = () => {},
  classNames = '',
  inputClassNames = '',
  autoFocus = false,
  maxResults = 10,
  formatResult,
  enableSearchIcon = true,
  enableEditIcon = false,
  enableClearIcon = true,
  editIconStyle = '',
  searchIconStyle = '',
  clearIconStyle = '',
  debounceTime = 0,
}) => {
  const inputRef = useRef(null)
  const debouncedSearch = debounce(onSearch, debounceTime)

  // Animation variants
  const container = {
    hidden: { opacity: 0, height: 0 },
    show: {
      opacity: 1,
      height: 'auto',
      transition: {
        height: {
          duration: 0.4,
        },
        staggerChildren: 0.1,
      },
    },
    exit: {
      opacity: 0,
      height: 0,
      transition: {
        height: {
          duration: 0.3,
        },
        opacity: {
          duration: 0.2,
        },
      },
    },
  }

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
      },
    },
    exit: {
      opacity: 0,
      y: -10,
      transition: {
        duration: 0.2,
      },
    },
  }

  return (
    <Downshift
      onChange={(selectedItem, { clearSelection, closeMenu }) => {
        if (selectedItem) {
          onSelect(selectedItem)
          clearSelection()
          closeMenu() // Close dropdown after selection
        }
      }}
      itemToString={(item) => (item ? item.name : '')}
      id="downshift-search"
    >
      {({ getInputProps, getMenuProps, inputValue, highlightedIndex, isOpen, clearSelection, closeMenu }) => (
        <div className={`relative ${classNames}`}>
          <div className="relative">
            <input
              {...getInputProps({
                ref: inputRef,
                placeholder,
                autoFocus,
                onChange: (e) => {
                  const value = e.target.value
                  debouncedSearch(value)
                },
                onKeyDown: (e) => {
                  if (e.key === 'Enter') {
                    closeMenu()
                  }
                },
              })}
              className={`
                bg-[#F2F2F2]
                rounded-md 
                text-[13px] 
                w-full 
                p-2 
                pl-3 
                h-[44px] 
                text-center
                placeholder:text-[#3f3f3f]
                placeholder:text-center
                ${enableSearchIcon && enableClearIcon ? 'pr-20' : 'pr-10'}
                ${inputClassNames}
                focus:outline-none 
                focus:ring-1
                focus:ring-custom-voilet
              `}
            />

            {/* Clear Icon with Animation */}
            <AnimatePresence mode="popLayout">
              {enableClearIcon && inputValue?.length > 0 && (
                <motion.button
                  key="clear"
                  type="button"
                  onClick={() => {
                    clearSelection()
                    onSearch('')
                    closeMenu() // Close dropdown when clearing
                    if (inputRef.current) inputRef.current.focus()
                  }}
                  className={`absolute right-11  top-[12px] cursor-pointer ${clearIconStyle}`}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <RxCross2 className="w-5 h-5" />
                </motion.button>
              )}
            </AnimatePresence>

            {/* Edit Icon with Animation */}
            {enableEditIcon && (
              <motion.img
                src="/assets/common/pen.svg"
                className={`absolute ${enableSearchIcon ? 'right-10' : 'right-2'} cursor-pointer top-[14px] ${editIconStyle}`}
                width="16"
                height="16"
                onClick={onEditIconClick}
                alt="edit"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                transition={{ duration: 0.2 }}
              />
            )}

            {/* Search Icon with Animation */}
            {enableSearchIcon && (
              <motion.img
                src="/assets/common/search_icon.svg"
                className={`absolute right-2 top-[9px] ${searchIconStyle}`}
                width="24"
                height="24"
                alt="search"
                initial={{ opacity: 0.7 }}
                animate={{ opacity: inputValue?.length > 0 ? 0.5 : 0.7 }}
                transition={{ duration: 0.2 }}
              />
            )}
          </div>

          {/* Dropdown Menu with Animation */}
          <AnimatePresence>
            {items?.length > 0 && isOpen && (
              <motion.div
                className={`
                  mt-1
                  absolute 
                  w-full 
                  rounded-md 
                  shadow-lg
                  bg-white
                  border
                  overflow-hidden
                `}
                style={{
                  backdropFilter: 'blur(12px)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                  position: 'absolute',
                }}
                variants={container}
                initial="hidden"
                animate="show"
                exit="exit"
              >
                <ul
                  {...getMenuProps()}
                  className={`
                    list-none 
                    p-0 
                    max-h-[500px] 
                    overflow-y-auto
                  `}
                >
                  {inputValue?.length > 0 ? (
                    items
                      .filter((item) => item.name.toLowerCase().includes(inputValue.toLowerCase()))
                      .slice(0, maxResults)
                      .map((item, index) => (
                        <motion.li
                          key={item.alias || `item-${index}`}
                          className={`
                            transition-colors
                            rounded-md
                          `}
                          variants={item}
                          layout
                          transition={{ duration: 0.2 }}
                        >
                          <Link
                            href={`/model/${item.alias}`}
                            className={`
                              block
                              px-3 
                              py-2 
                              cursor-pointer 
                              ${highlightedIndex === index ? 'bg-[#dbdbdb]' : 'bg-white'}
                              bg-[#F2F2F2]
                              hover:bg-[#dbdbdb]
                              transition-colors
                              rounded-md
                            `}
                            onClick={() => {
                              // Still call onSelect for any additional handling if needed
                              onSelect(item)
                              closeMenu() // Close dropdown when item is clicked
                            }}
                          >
                            <span className="text-sm font-medium text-gray-900">
                              {formatResult ? formatResult(item) : item.name}
                            </span>
                          </Link>
                        </motion.li>
                      ))
                  ) : (
                    <motion.li className="p-3 text-sm text-gray-500" variants={item}>
                      <span className="text-sm font-medium text-gray-500">Start typing to search...</span>
                    </motion.li>
                  )}
                  {inputValue?.length > 0 &&
                    items.length > 0 &&
                    !items.some((item) => item.name.toLowerCase().includes(inputValue.toLowerCase())) && (
                      <motion.li className="p-3 text-sm text-gray-500" variants={item}>
                        <span className="text-sm font-medium text-gray-500">No results found</span>
                      </motion.li>
                    )}
                </ul>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </Downshift>
  )
}

export default SearchAutoComplete
